import {copyText} from '@github-ui/copy'
import {updateUrlHash} from '@github-ui/history'
import type {
  SourceRange as TaillightsSourceRange,
  SourceSelection as TaillightsSourceSelection,
} from '@github-ui/taillights-worker/document'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {
  anchorFromSelection,
  gutterSelection,
  lastSelectedLine,
  lineRange,
  selectedText,
  type SourceRange,
} from './source-navigation'
import type {TaillightsGutterAction} from './TaillightsGutterRow'
import type {useSourceNavigation} from './use-source-navigation'

type Snapshot = {range: TaillightsSourceSelection & SourceRange; text: string}
type OverlayOwner = {
  lineNumber: number
  kind: 'menu' | 'annotation'
  snapshot: Snapshot
  projection: readonly number[]
  returnTo: HTMLElement | null
}

export function useGutterSelection({
  navigation,
  reportMessage,
  highlightLines,
  showLineActions,
}: {
  navigation: ReturnType<typeof useSourceNavigation>
  reportMessage: (message: string) => void
  highlightLines: (range: TaillightsSourceRange) => void
  showLineActions: boolean
}) {
  const {lines, visible, anchorRange, setAnchorRange, symbolsRef, textareaRef} = navigation
  const [owner, setOwner] = useState<OverlayOwner | null>(null)
  const [focusReturn, setFocusReturn] = useState<{target: HTMLElement | null} | null>(null)
  const snapshotRef = useRef<Snapshot | null>(null)
  const focusFrameRef = useRef<number | null>(null)

  const readSnapshot = useCallback((): Snapshot => {
    const range = symbolsRef.current?.getSelection() ?? {...(anchorRange ?? lineRange(lines, 0)), direction: 'none'}
    const textarea = textareaRef.current
    return {
      // Hash navigation selects native text, but matching endpoints retain the explicit line-range intent.
      range:
        anchorRange?.endLineInclusive &&
        range.start.line === anchorRange.start.line &&
        range.start.column === anchorRange.start.column &&
        range.end.line === anchorRange.end.line &&
        range.end.column === anchorRange.end.column
          ? {...range, endLineInclusive: true}
          : range,
      text: textarea ? textarea.value.slice(textarea.selectionStart, textarea.selectionEnd) : '',
    }
  }, [symbolsRef, anchorRange, lines, textareaRef])

  const captureSnapshot = useCallback(() => {
    snapshotRef.current = readSnapshot()
  }, [readSnapshot])

  const restoreFocus = useCallback(
    (target: HTMLElement | null) => {
      if (focusFrameRef.current !== null) cancelAnimationFrame(focusFrameRef.current)
      // Primer restores its captured anchor during overlay cleanup. Restore the source initiator afterwards.
      focusFrameRef.current = requestAnimationFrame(() => {
        focusFrameRef.current = null
        const fallback = textareaRef.current
        ;(target?.isConnected ? target : fallback)?.focus({preventScroll: true})
      })
    },
    [textareaRef],
  )

  useEffect(
    () => () => {
      if (focusFrameRef.current !== null) cancelAnimationFrame(focusFrameRef.current)
    },
    [],
  )

  // A fold/projection change makes an action snapshot stale, even if its header remains visible.
  if (owner && (owner.projection !== visible || (owner.kind === 'menu' && !showLineActions))) {
    setOwner(null)
    setFocusReturn({target: null})
  }

  // TODO: Explore Primer's returnFocusRef to replace this handoff, preserving source/gutter
  // targets, Tab navigation, and textarea fallback when projection changes remove the target.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Synchronize focus after overlay cleanup, including externally invalidated projections.
    if (focusReturn) restoreFocus(focusReturn.target)
  }, [focusReturn, restoreFocus])

  const returnTo = owner?.returnTo
  const close = useCallback(
    (reason?: 'tab') => {
      if (reason === 'tab') {
        if (focusFrameRef.current !== null) cancelAnimationFrame(focusFrameRef.current)
        focusFrameRef.current = null
        setFocusReturn(null)
      } else if (returnTo) setFocusReturn({target: returnTo})
      setOwner(null)
    },
    [returnTo],
  )

  const openAt = useCallback(
    (lineNumber: number, kind: OverlayOwner['kind'], target: HTMLElement | null, snapshot: Snapshot) => {
      if (focusFrameRef.current !== null) cancelAnimationFrame(focusFrameRef.current)
      setFocusReturn(null)
      const inSelection =
        lineNumber - 1 >= snapshot.range.start.line && lineNumber - 1 <= lastSelectedLine(snapshot.range)
      const inHighlight =
        anchorRange && lineNumber - 1 >= anchorRange.start.line && lineNumber - 1 <= lastSelectedLine(anchorRange)
      let range = inSelection ? snapshot.range : {...lineRange(lines, lineNumber - 1), direction: 'none' as const}
      if (inHighlight && (!inSelection || !snapshot.text)) range = {...anchorRange, direction: 'none'}
      setOwner({
        lineNumber,
        kind,
        snapshot: {range, text: inSelection ? snapshot.text : ''},
        projection: visible,
        returnTo: target,
      })
    },
    [anchorRange, lines, visible],
  )

  const openMenu = useCallback(
    (useSnapshot = false) => {
      if (!showLineActions) return
      if (!visible.length) {
        reportMessage('No source lines available.')
        return
      }
      const snapshot = (useSnapshot && snapshotRef.current) || readSnapshot()
      snapshotRef.current = null
      const range =
        !snapshot.text && anchorRange && document.activeElement !== textareaRef.current ? anchorRange : snapshot.range
      const line = snapshot.range.direction === 'backward' ? range.start.line : lastSelectedLine(range)
      const visibleLine = visible.includes(line) ? line : (visible.findLast(item => item <= line) ?? 0)
      openAt(visibleLine + 1, 'menu', document.activeElement as HTMLElement | null, snapshot)
    },
    [showLineActions, visible, reportMessage, readSnapshot, anchorRange, textareaRef, openAt],
  )

  const setOverlay = useCallback(
    (lineNumber: number, kind: OverlayOwner['kind'], open: boolean, closeReason?: 'tab') => {
      if (open && kind === 'menu' && !showLineActions) return
      if (!open) {
        close(closeReason)
        return
      }
      openAt(lineNumber, kind, null, snapshotRef.current ?? readSnapshot())
      snapshotRef.current = null
    },
    [showLineActions, close, openAt, readSnapshot],
  )

  const onLine = useCallback(
    (lineNumber: number, extend: boolean) => {
      const snapshot = snapshotRef.current ?? readSnapshot()
      snapshotRef.current = null
      const selection = gutterSelection(lines, lineNumber - 1, extend, snapshot.range, anchorRange)
      if (selection.preserve) {
        setAnchorRange(selection.range)
        updateUrlHash(anchorFromSelection(selection.range, lines))
        symbolsRef.current?.setSelection(selection.preserve)
      } else {
        highlightLines(selection.range)
      }
    },
    [readSnapshot, lines, anchorRange, setAnchorRange, symbolsRef, highlightLines],
  )

  const ownerSnapshot = owner?.snapshot
  const copy = useCallback(
    async (kind: 'selection' | 'lines' | 'permalink', providedSnapshot?: Snapshot) => {
      const snapshot = providedSnapshot === undefined ? (ownerSnapshot ?? readSnapshot()) : providedSnapshot
      const {range} = snapshot
      const fullLines = lineRange(lines, range.start.line, lastSelectedLine(range))
      let text = kind === 'lines' ? selectedText(lines, fullLines) : snapshot.text || selectedText(lines, fullLines)
      if (kind === 'permalink') {
        const url = new URL(window.location.href, window.location.origin)
        url.hash = anchorFromSelection(range, lines)
        text = url.href
      }
      try {
        await copyText(text)
        reportMessage(
          kind === 'permalink'
            ? 'Copied source permalink.'
            : kind === 'lines'
              ? 'Copied original source lines.'
              : 'Copied selected text or current line.',
        )
      } catch {
        reportMessage('Clipboard access failed. Select the code and use your browser’s Copy command.')
      }
    },
    [ownerSnapshot, readSnapshot, lines, reportMessage],
  )

  const range = useMemo(
    () => ownerSnapshot?.range ?? anchorRange ?? lineRange(lines, 0),
    [ownerSnapshot, anchorRange, lines],
  )
  const lastLine = lastSelectedLine(range)
  const singleLine = range.start.line === lastLine

  const onMenuOpenChange = useCallback(
    (line: number, open: boolean, closeReason?: 'tab') => setOverlay(line, 'menu', open, closeReason),
    [setOverlay],
  )
  const onAnnotationOpenChange = useCallback(
    (line: number, open: boolean) => setOverlay(line, 'annotation', open),
    [setOverlay],
  )
  const copyPermalink = useCallback(() => void copy('permalink', readSnapshot()), [copy, readSnapshot])
  const menuLabel = singleLine
    ? `Line ${range.start.line + 1} actions`
    : `Lines ${range.start.line + 1}–${lastLine + 1} actions`
  return useMemo(
    () => ({
      owner,
      range,
      singleLine,
      copy,
      setFocusReturn,
      menuLabel,
      captureSnapshot,
      onLine,
      openMenu,
      onMenuOpenChange,
      onAnnotationOpenChange,
      copyPermalink,
    }),
    [
      owner,
      range,
      singleLine,
      copy,
      menuLabel,
      captureSnapshot,
      onLine,
      openMenu,
      onMenuOpenChange,
      onAnnotationOpenChange,
      copyPermalink,
    ],
  )
}

export type GutterSelection = ReturnType<typeof useGutterSelection>
export type GutterActions = Pick<
  GutterSelection,
  'owner' | 'menuLabel' | 'captureSnapshot' | 'onLine' | 'onMenuOpenChange' | 'onAnnotationOpenChange'
> & {
  actions: readonly TaillightsGutterAction[]
  /** Undefined enables every row; null hides triggers until a line is selected. */
  actionLine?: number | null
}
